import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import Marquee from 'react-fast-marquee';
import {
  Shield, Database, Cloud, Settings, HeartPulse, Monitor,
  ChevronRight, Star, Award, TrendingUp,
  Wrench, Layers, Zap, Lock, Server, Headphones,
} from 'lucide-react';
import { useDynamicContent, usePublicSeo } from '@/hooks/useDynamicContent';
import { resolveIcon } from '@/utils/iconMap';
import Seo from '@/seo/Seo';

const HERO_BG = 'https://images.unsplash.com/photo-1765990153176-582ca8808384?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBnb2xkJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3NzU5NTM0OTJ8MA&ixlib=rb-4.1.0&q=85';

const ICON_SIZE_LG = 26;
const ICON_SIZE_MD = 22;

// Default content (used as a guaranteed fallback if API fails). Keys mirror seed.py.
const DEFAULTS = {
  hero_badge: 'Deployment | Sustainment | Cybersecurity | Mission Support',
  hero_title: 'Mission-Critical Deployment,',
  hero_title_gold: 'Sustainment & Operations Support',
  hero_description: "SCORE Integrated Solutions LLC delivers mission-focused technology deployment, sustainment, cybersecurity, and operational support services for federal and enterprise environments. We help organizations maintain secure, reliable, and scalable operations through integrated technology and mission-driven support solutions.",
  certifications: ['CMMI', 'ISO 27001', '8(a) Certified', 'HUBZone'],
  integrated_services_heading: 'Structured Support Across Four Core Areas',
  integrated_services_description: "Our services are organized around the areas where operational environments need consistent, dependable support. Each area is staffed by professionals who understand how these systems work in practice.",
  integrated_services: [
    { icon: 'Monitor', title: 'Technology Support Services', desc: 'Enterprise systems support, application environments, user support operations, and technology coordination for complex operational settings.' },
    { icon: 'Wrench', title: 'IT Sustainment & Operations', desc: 'Ongoing systems support, operational continuity, lifecycle coordination, and environment management to keep critical systems running reliably.' },
    { icon: 'Shield', title: 'Cybersecurity Support', desc: 'Security coordination, compliance support, access management, and operational risk reduction across secure enterprise environments.' },
    { icon: 'Settings', title: 'Program & Mission Support', desc: 'Program coordination, reporting, scheduling, documentation support, and stakeholder management for operational environments.' },
  ],
  services_overview_heading: 'Support Services',
  services_overview: [
    { icon: 'Monitor', title: 'Technology Support', desc: 'Enterprise systems support, user operations, and technology coordination.' },
    { icon: 'Wrench', title: 'Systems Sustainment', desc: 'Lifecycle support, operational continuity, and environment management.' },
    { icon: 'Shield', title: 'Cybersecurity Support', desc: 'Security coordination, compliance support, and access management.' },
    { icon: 'Settings', title: 'Program Support', desc: 'Operational coordination, reporting, and process management.' },
    { icon: 'Cloud', title: 'Cloud & Infrastructure', desc: 'Cloud environment support, infrastructure management, and migration.' },
    { icon: 'HeartPulse', title: 'Healthcare IT Support', desc: 'Clinical systems support, EHR environments, and health IT operations.' },
    { icon: 'Database', title: 'Data & Reporting', desc: 'Data management, reporting support, and analytics coordination.' },
    { icon: 'Server', title: 'Enterprise Services', desc: 'Business process support and enterprise coordination services.' },
    { icon: 'Headphones', title: 'User Support Operations', desc: 'Service desk support, issue tracking, and end-user assistance.' },
  ],
  differentiators_heading: 'How We Operate',
  differentiators: [
    { icon: 'Layers', title: 'Operational Depth', desc: 'Support services grounded in real operational environments.' },
    { icon: 'TrendingUp', title: 'Mission Continuity', desc: 'Focused on keeping systems running and operations moving.' },
    { icon: 'Award', title: 'Federal Experience', desc: 'Hands-on support within complex government programs.' },
    { icon: 'Lock', title: 'Secure & Reliable', desc: 'Built around compliance, security, and long-term stability.' },
    { icon: 'Zap', title: 'Responsive Support', desc: 'Structured to adapt quickly when requirements change.' },
  ],
  experience_items: ['U.S. ARMY', 'DEFENSE HEALTH AGENCY', 'VETERANS AFFAIRS', 'DEFENSE LOGISTICS AGENCY', 'MHS GENESIS', 'FEDERAL AGENCIES'],
  testimonials_heading: 'What Our Partners Say',
  testimonials: [
    { quote: "Their team integrated smoothly into our operations and kept our systems running through a difficult transition period. Reliable and professional throughout.", name: 'Col. James Mitchell', role: 'Program Director, U.S. Army', rating: 5 },
    { quote: 'Score Integrated Solutions helped us get our cybersecurity posture where it needed to be. They understood the requirements and delivered without unnecessary complexity.', name: 'Dr. Sarah Chen', role: 'Deputy CTO, Defense Health Agency', rating: 5 },
    { quote: 'We needed sustained technology support across multiple environments, and they delivered consistently. Good communication, reliable follow-through.', name: 'Robert Alvarez', role: 'IT Branch Chief, Veterans Affairs', rating: 5 },
  ],
  stats: [
    { value: '15+', label: 'Years Experience' },
    { value: '50+', label: 'Federal Projects' },
    { value: '99%', label: 'Client Satisfaction' },
    { value: '24/7', label: 'Support Available' },
  ],
  cta_heading: 'Ready to Strengthen Your Operations?',
  cta_description: "If your organization needs dependable technology support, systems sustainment, or operational coordination, we're ready to help. Let's discuss what you need.",
};

// Legacy fallbacks so the static export shape is preserved (lucide imports retained for tree-shake awareness)
void [Shield, Database, Cloud, Settings, HeartPulse, Monitor, Star, Award, TrendingUp, Wrench, Layers, Zap, Lock, Server, Headphones];

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`${className} relative z-10 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      {children}
    </section>
  );
}

function TestimonialCard({ testimonial, index }) {
  return (
    <div data-testid={`testimonial-${index}`} className="bg-[#161616] border border-white/10 p-8 card-hover gold-border-glow transition-all">
      <div className="flex gap-1 mb-5">
        {Array.from({ length: testimonial.rating || 5 }).map((_, j) => (
          <Star key={`star-${j}`} size={14} className="text-[#F2B705] fill-[#F2B705]" />
        ))}
      </div>
      <p className="text-sm text-[#A3A3A3] leading-relaxed mb-6 italic">"{testimonial.quote}"</p>
      <div>
        <p className="text-sm font-semibold text-white">{testimonial.name}</p>
        <p className="text-xs text-[#525252] mt-1">{testimonial.role}</p>
      </div>
    </div>
  );
}

function StatItem({ value, label, index }) {
  return (
    <div data-testid={`stat-${index}`}>
      <p className="text-3xl md:text-4xl font-bold text-[#F2B705]">{value}</p>
      <p className="text-xs uppercase tracking-widest text-[#A3A3A3] mt-2">{label}</p>
    </div>
  );
}

function IntegratedServiceCard({ service, index }) {
  const Icon = resolveIcon(service.icon, Monitor);
  return (
    <div data-testid={`integrated-service-${index}`} className="relative p-10 border border-white/10 group hover:bg-[#161616] transition-all gold-border-glow">
      <div className="flex items-start gap-5">
        <div className="w-14 h-14 border border-[#F2B705]/30 flex items-center justify-center shrink-0 group-hover:border-[#F2B705] transition-colors">
          <Icon className="text-[#F2B705]" size={ICON_SIZE_LG} strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
          <p className="text-sm text-[#A3A3A3] leading-relaxed">{service.desc}</p>
        </div>
      </div>
    </div>
  );
}

function ServiceGridCard({ service, index }) {
  const Icon = resolveIcon(service.icon, Monitor);
  return (
    <div data-testid={`service-card-${index}`} className="service-grid-item p-8 group hover:bg-[#1A1A1A] transition-colors">
      <Icon className="text-[#F2B705] mb-5" size={28} strokeWidth={1.5} />
      <h3 className="text-lg font-semibold text-white mb-3">{service.title}</h3>
      <p className="text-sm text-[#A3A3A3] leading-relaxed">{service.desc}</p>
    </div>
  );
}

function DifferentiatorCard({ item, index }) {
  const Icon = resolveIcon(item.icon, Layers);
  return (
    <div data-testid={`why-card-${index}`} className="bg-[#161616] border border-white/10 p-6 card-hover gold-border-glow transition-all text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 border border-[#F2B705]/30 mb-4">
        <Icon className="text-[#F2B705]" size={ICON_SIZE_MD} strokeWidth={1.5} />
      </div>
      <h3 className="text-sm font-semibold text-white mb-2">{item.title}</h3>
      <p className="text-xs text-[#A3A3A3] leading-relaxed">{item.desc}</p>
    </div>
  );
}

export default function HomePage() {
  const c = useDynamicContent('home', DEFAULTS);
  const seoOverrides = usePublicSeo('home');

  return (
    <div className="min-h-screen bg-[#121212] relative">
      <Seo
        page="home"
        title={seoOverrides?.title}
        description={seoOverrides?.description}
        keywords={seoOverrides?.keywords}
        ogTitle={seoOverrides?.og_title}
        ogDescription={seoOverrides?.og_description}
      />
      <div className="fixed inset-0 z-0 pointer-events-none">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-[0.06]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/30 via-transparent to-[#121212]/50" />
      </div>

      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center z-10">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-32 pb-20">
          <div className="max-w-3xl">
            <div className="animate-fade-in-up">
              <span className="inline-block text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold mb-6 border border-[#F2B705]/30 px-4 py-1.5">
                {c.hero_badge}
              </span>
            </div>
            <h1 className="animate-fade-in-up delay-100 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-white mb-6" style={{ opacity: 0 }}>
              {c.hero_title}{' '}
              <span className="gold-gradient-text">{c.hero_title_gold}</span>
            </h1>
            <p className="animate-fade-in-up delay-200 text-base md:text-lg text-[#A3A3A3] leading-relaxed max-w-2xl mb-10" style={{ opacity: 0 }}>
              {c.hero_description}
            </p>
            <div className="animate-fade-in-up delay-300 flex flex-wrap gap-4" style={{ opacity: 0 }}>
              <Link to="/contact" data-testid="hero-contact-btn" className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-8 py-4 hover:bg-[#D9A404] transition-colors flex items-center gap-2">
                Contact Us <ChevronRight size={16} />
              </Link>
              <Link to="/services" data-testid="hero-services-btn" className="border border-white/20 text-white font-bold text-sm uppercase tracking-wide px-8 py-4 hover:border-[#F2B705] hover:text-[#F2B705] transition-all">
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <Section className="py-16 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap justify-center gap-6 md:gap-10">
            {(c.certifications || []).map((cert) => (
              <div key={cert} data-testid={`cert-badge-${cert.toLowerCase().replace(/[^a-z0-9]/g, '-')}`} className="border border-[#F2B705]/30 px-6 py-3 text-xs tracking-widest uppercase font-bold text-[#F2B705] hover:bg-[#F2B705]/5 transition-colors">
                {cert}
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Integrated Services */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">What We Do</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">{c.integrated_services_heading}</h2>
            <p className="text-sm text-[#A3A3A3] mt-4 max-w-2xl leading-relaxed">{c.integrated_services_description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {(c.integrated_services || []).map((svc, i) => (
              <IntegratedServiceCard key={svc.title || i} service={svc} index={i} />
            ))}
          </div>
        </div>
      </Section>

      {/* Services Overview */}
      <Section className="py-24 md:py-32 bg-[#161616]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Our Capabilities</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">{c.services_overview_heading}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {(c.services_overview || []).map((svc, i) => (
              <ServiceGridCard key={svc.title || i} service={svc} index={i} />
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/services" data-testid="services-view-all-btn" className="text-sm uppercase tracking-wide text-[#F2B705] font-bold hover:underline underline-offset-4 inline-flex items-center gap-2">
              View All Services <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </Section>

      {/* Differentiators */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Why Work With Us</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">{c.differentiators_heading}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {(c.differentiators || []).map((item, i) => (
              <DifferentiatorCard key={item.title || i} item={item} index={i} />
            ))}
          </div>
        </div>
      </Section>

      {/* Experience Marquee */}
      <Section className="py-20 border-y border-white/10 overflow-hidden">
        <div className="mb-8 text-center">
          <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Trusted By</span>
        </div>
        <Marquee speed={40} gradient={false} pauseOnHover>
          {(c.experience_items || []).map((item, i) => (
            <span key={item || i} data-testid={`exp-item-${i}`} className="text-3xl md:text-5xl font-bold tracking-tighter text-[#2A2A2A] hover:text-[#F2B705] transition-colors duration-300 mx-12 cursor-default select-none">
              {item}
            </span>
          ))}
        </Marquee>
      </Section>

      {/* Testimonials */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Testimonials</span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">{c.testimonials_heading}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(c.testimonials || []).map((t, i) => (
              <TestimonialCard key={t.name || i} testimonial={t} index={i} />
            ))}
          </div>
        </div>
      </Section>

      {/* Stats */}
      <Section className="py-20 bg-[#161616] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {(c.stats || []).map((stat, i) => (
              <StatItem key={stat.label || i} value={stat.value} label={stat.label} index={i} />
            ))}
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">{c.cta_heading}</h2>
          <p className="text-base text-[#A3A3A3] max-w-2xl mx-auto mb-10">{c.cta_description}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-contact-btn" className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-10 py-4 hover:bg-[#D9A404] transition-colors flex items-center gap-2">
              Request Consultation <ChevronRight size={16} />
            </Link>
            <Link to="/about" data-testid="cta-about-btn" className="border border-white/20 text-white font-bold text-sm uppercase tracking-wide px-10 py-4 hover:border-[#F2B705] hover:text-[#F2B705] transition-all">
              Learn About Us
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
